import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, Observable, of, retry, take, tap, throwError } from 'rxjs';
import { ApiRouter } from '../../../../global/routes/api-routes';
import { BOOKING_DATA, SELECTED_APPOINTMENT_DATA } from '../constants';
import { GetRebookingDataResponse } from '../types/get-rebooking-data-response';
import { GetRepairDataResponse } from '../types/get-repair-data-response';
import { PutRebookingDataResponse } from '../types/put-rebooking-data-response';

export type RebookingErrorType = 'generic' | 'timeslot-taken' | 'max-retries-exceeded';

@Injectable({ providedIn: 'root' })
export class RebookingService {
  formError = signal<HttpErrorResponse | undefined>(undefined);
  isLoading = signal<boolean>(false);
  submittingValue = signal(false);
  formValid = signal(false);
  planNumber = signal<string>('');
  claimId = signal<string>('');
  selectedAppointmentData = signal<PutRebookingDataResponse | undefined>(undefined);
  bookingData = signal<GetRepairDataResponse | undefined>(undefined);
  readonly errorType = signal<RebookingErrorType | undefined>(undefined);
  readonly retryAttempts = signal(0);

  private readonly retryableErrorCodes = new Set(['PN008', 'PN004', 'PN003']);
  public readonly timeslotTakenCode = 'PN007';
  private readonly maxRetryCount = 3;

  constructor(private readonly http: HttpClient) {}

  clearErrorState(): void {
    this.formError.set(undefined);
    this.errorType.set(undefined);
    this.retryAttempts.set(0);
  }

  clearTimeslotError(): void {
    if (this.errorType() === 'timeslot-taken') {
      this.errorType.set(undefined);
      this.formError.set(undefined);
    }
  }

  setFormError(error: HttpErrorResponse | undefined): void {
    this.formError.set(error);
  }

  private handleError(error: HttpErrorResponse): void {
    this.setFormError(error);
    const errorCode = error?.error?.status;

    if (errorCode === this.timeslotTakenCode) {
      this.errorType.set('timeslot-taken');
      return;
    }

    if (this.isRetryable(errorCode)) {
      this.errorType.set('max-retries-exceeded');
      return;
    }

    this.errorType.set('generic');
  }

  private isRetryable(errorCode: string | undefined): boolean {
    return errorCode ? this.retryableErrorCodes.has(errorCode) : false;
  }

  private rebookingState = computed(() => ({
    claimId: this.claimId(),
    planNumber: this.planNumber(),
    appointmentData: this.getRebookingData.value(),
    selectedAppointmentData: this.selectedAppointmentData(),
    bookingData: this.bookingData(),
  }));

  get state() {
    return this.rebookingState();
  }

  getRebookingData = rxResource({
    params: () => {
      const claimId = this.claimId();
      return claimId || undefined;
    },
    stream: ({ params }) => {
      if (!params) {
        return of(undefined);
      }

      this.clearErrorState();
      this.retryAttempts.set(0);

      return this.http.get<GetRebookingDataResponse>(ApiRouter.rebooking(this.planNumber(), params)).pipe(
        take(1),
        retry({
          count: this.maxRetryCount,
          delay: (error, retryCount) => {
            const errorCode = error?.error?.status;
            if (this.isRetryable(errorCode)) {
              this.retryAttempts.set(retryCount);
              return of(true);
            }
            throw error;
          },
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return throwError(() => error);
        })
      );
    },
  });

  putRebookingData(
    claimId: string,
    appointmentSlotIdentifier: string,
    appointmentNotes: string = '',
    bringForwardFlag: boolean = false
  ): Observable<PutRebookingDataResponse> {
    this.clearErrorState();
    this.retryAttempts.set(0);

    return this.http
      .put<PutRebookingDataResponse>(ApiRouter.orbit.putRebookingData(), {
        claimId,
        appointmentSlotIdentifier,
        appointmentNotes,
        bringForwardFlag,
      })
      .pipe(
        tap((data) => {
          sessionStorage.setItem(SELECTED_APPOINTMENT_DATA, JSON.stringify(data));
          this.selectedAppointmentData.set(data);
        }),
        retry({
          count: this.maxRetryCount,
          delay: (error, retryCount) => {
            const errorCode = error?.error?.status;
            if (this.isRetryable(errorCode)) {
              this.retryAttempts.set(retryCount);
              return of(true);
            }
            throw error;
          },
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(() => error);
        })
      );
  }

  getRepairData(claimID: string) {
    return this.http.get<GetRepairDataResponse>(ApiRouter.getRepairData(this.state.planNumber, claimID)).pipe(
      tap((data) => {
        const faultSelected = this.formatFault(data.faultSelected);
        const formattedData = { ...data, faultSelected };
        sessionStorage.setItem(BOOKING_DATA, JSON.stringify(formattedData));
        this.bookingData.set(formattedData);
      }),
      catchError((error) => {
        this.setFormError(error);
        return throwError(() => error);
      })
    );
  }

  formatFault(faultString: string = '') {
    const numberString = faultString.match(/^\d+\s*-\s*(.*)$/);
    return numberString?.[1]?.trim() ?? faultString.trim();
  }
}
